import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom } from 'rxjs';
import merge from 'lodash/merge';

@Injectable({
	providedIn: 'root',
})
export class ConfigService {
	configsLoadedEvent: EventEmitter<void> = new EventEmitter<void>();
	
	public configLoadedPromise: Promise<void>;
	private resolveConfigLoaded!: () => void;

	svc: any = {};
	system: any = {};

	private auditOperationEnabledMap: Map<string, boolean> = new Map();
	private shareLinkState: boolean | null = null;
	private shareLinkStateFetched = false;

	constructor(private http: HttpClient, private modalConfig: NgbModalConfig) {
		this.configLoadedPromise = new Promise((resolve) => {
			this.resolveConfigLoaded = resolve;
		});

		this.loadConfiguration();

		// Set default modal options
		this.modalConfig.container = '#modal-holder';
		this.modalConfig.backdrop = 'static';
		this.modalConfig.size = 'lg';
		this.modalConfig.keyboard = false;
		this.modalConfig.animation = false;
		this.modalConfig.backdropClass = 'kv-modal-backdrop';
		this.modalConfig.windowClass = 'kv-modal-window';
		this.modalConfig.ariaLabelledBy = 'modal-title-id';
		console.log('Global modal configuration initialized');
	}

	/**
	 * Loads the configuration from the server.
	 */
	async loadConfiguration() {
        await this.loadConfig();
        try {
        	const response: any = await firstValueFrom(this.http.get('/aw-server/rest/admin/configuration'));
        	if (response?.prefs) {
				console.log('Loaded configuration for domain: %s', response.domain);

				let jsonConfig = JSON.parse(response.prefs);
				merge(this.svc, jsonConfig);

				this.resolveConfigLoaded();
				this.configsLoadedEvent.emit();
			}
        } catch (error) {
		    console.error('Failed to retrieve main configuration.', error);
		    if (
			    typeof error === 'object' &&
			    error !== null &&
			    'status' in error &&
			    typeof (error as any).status === 'number' &&
			    typeof (error as any).error === 'string' &&
			    (error as any).status === 401 &&
			    (error as any).error.includes('Client certificate required')
			  ) {
				window.location.href = '/custom-cert-error.html';
			}
		}

		// Load the server settings configuration
		try {
			const response: any = await firstValueFrom(this.http.get('/aw-server/rest/admin/configuration/server/settings'));
			this.system = response;
		} catch (error) {
		    console.error('Failed to retrieve server settings configuration.', error);
		}
	}

	private async loadConfig(): Promise<void> {
		return firstValueFrom(this.http.get('/app/aw-ui-angular/assets/config/config.json'))
			.then(config => {
				this.svc = config;
				if (!this.svc.GLOBAL) this.svc.GLOBAL = config;
			}).catch(() => {
				console.error("Failed to load config.json");
				this.svc.GLOBAL = {};
			});
	}
  
	waitForConfigLoaded(): Promise<void> {
		return this.configLoadedPromise;
	}

	/**
	 * Returns the current configuration.
	 */
	getConfig() {
		return this.svc;
	}

	
	// TODO: Move this to metadata.service
	/**
	 * Returns the template type used by the controller providing the Metadata View.
	 */
	getMetadataTemplateType(): string {
		let config = this.getConfig();
		return config?.ui?.metadataView?.mode || 'standard';
	}

	/**
	 * Returns the URL for the custom JSON used to present the data shown in the
	 * Metadata View, or undefined.
	 */
	getMetadataCustomTemplateURL(): string | undefined {
		let config = this.getConfig();
		if (config?.ui?.metadataView?.mode === 'template') {
			let tmpl = config?.ui?.metadataView?.template || 'sample.json';
			return `~/../app/load/templates/metadata/${tmpl}`;
		}
		return undefined;
	}

	
	/**
	 * Returns the status of the share link provider.
	 */
	getShareLinkState(): boolean {
		if (!this.shareLinkStateFetched) {
			this.fetchShareLinkState();
		}

		return this.shareLinkState ?? false;
	}

	private fetchShareLinkState(): void {
		let config = this.getConfig();
		let url = config?.document?.productShareLinkEnabledURL;

		this.http.get<any>(url).subscribe({
			next: (response) => {
				this.shareLinkState = true;
				this.shareLinkStateFetched = true;
			},
			error: (error) => {
				this.shareLinkState = false;
				this.shareLinkStateFetched = true;
			}
		});
	}
	
	/**
	 * Returns the audit reporting status of a given KV operation.
	 */	
	async getAuditStatus(operationName: string): Promise<boolean> {
		let enabled = this.auditOperationEnabledMap.get(operationName);
		if (enabled === undefined) {
			enabled = await this.fetchAuditStatus(operationName);
			this.auditOperationEnabledMap.set(operationName, enabled);
		}
		return enabled;	
	}
	
	private async fetchAuditStatus(operationName: string): Promise<boolean> {
		let url = `/aw-server/rest/reports/audited`;
		
		let operationNames = [operationName];

		try {
			return await firstValueFrom(this.http.post<boolean>(url, operationNames));
		} catch (error) {
			return false;
		}
	}	
}
